import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Output,
  QueryList,
  ViewChildren
} from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AdHocProductPage } from '../../ad-hoc-product/ad-hoc-product.page';
import { AuthUserService } from '../../auth/auth-user.service';
import { CashRegisterActionsComponent } from '../../cashbox/cash-register-actions.component';
import { CustomerService } from '../../customer/customer.service';
import { DataService } from '../../data/data.service';
import { FEATURE_FLAGS } from '../../data/models/models';
import { PrinterService } from '../../data/printer.service';
import { DepositWithdrawPage } from '../../deposit-withdraw/deposit-withdraw.page';
import { ReservationPage } from '../../reservation/reservation.page';
import { TransactionHistoryPage } from '../../transaction-history/transaction-history.page';
import { ExternalRoutesService } from '../external-redirect';
import { SCREEN_LAYOUT, ScreenLayout } from '../layout/layout.provider';
import { PrinterStatusComponent } from './printer-status.component';
import { SidebarButtonComponent } from './sidebar-button.component';
import { VersionMismatchModalComponent } from './version-mismatch-modal.component';
import { version } from './../../../environments/version';
import { environment } from './../../../environments/environment';

@Component({
    selector: 'app-navbar',
    template: `
    <div class="d-flex flex-column h-100 bg-white rounded-end-6px">
      <ng-content select="[start]"></ng-content>

      <div class="d-flex justify-content-start flex-column flex-grow-1 align-items-center overflow-y-auto">
        <app-sidebar-button icon="storefront" text="Produkte" i18n-text routerLink="/"
                            [disabled]="dataService.cashBoxClosed"></app-sidebar-button>

        <app-sidebar-button icon="point_of_sale" text="Kasse" i18n-text (click)="openCashRegistry()"
                            [disabled]="dataService.cashBoxClosed"></app-sidebar-button>

        <ng-container *appFeature="FEATURE_FLAGS.SALES_HISTORY">
          <app-sidebar-button icon="schedule" text="Historie" i18n-text
                              [disabled]="dataService.cashBoxClosed"
                              *ngIf="layout === 'mobile'" [routerLink]="['transaction-history']"></app-sidebar-button>
          <app-sidebar-button icon="schedule" text="Historie" i18n-text
                              [disabled]="dataService.cashBoxClosed"
                              *ngIf="layout === 'desktop'" (click)="openTransactionHistory()"></app-sidebar-button>
        </ng-container>

        <ng-container *appFeature="FEATURE_FLAGS.RESERVATIONS">
          <app-sidebar-button icon="shopping_cart_checkout" text="Abholungen" i18n-text
                              (click)="openReservations()"
                              *ngIf="layout === 'desktop'"
                              [badge]="dataService.reservationsAndPickupsCount"
                              [disabled]="dataService.cashBoxClosed"></app-sidebar-button>
          <app-sidebar-button icon="shopping_cart_checkout" text="Abholungen" i18n-text
                              [disabled]="dataService.cashBoxClosed"
                              *ngIf="layout === 'mobile'"
                              [badge]="dataService.reservationsAndPickupsCount"
                              [routerLink]="['reservations']"></app-sidebar-button>
        </ng-container>

        <app-sidebar-button icon="add_shopping_cart" text="Adhoc Produkt" i18n-text *appFeature="FEATURE_FLAGS.ADHOC_PRODUCTS"
                            (click)="openAdHocProduct()"
                            [disabled]="dataService.cashBoxClosed"></app-sidebar-button>

        <app-sidebar-button icon="person_add_alt_1" text="Kunden erstellen" i18n-text *appFeature="FEATURE_FLAGS.CUSTOMERS"
                            (click)="openNewCustomerModal()"></app-sidebar-button>

        <app-sidebar-button icon="calculate" text="Ein/Auszahlung" i18n-text *appFeature="FEATURE_FLAGS.MONEY_MANAGEMENT"
                            (click)="openDepositWithdraw()"
                            [disabled]="dataService.cashBoxClosed"></app-sidebar-button>

        <app-sidebar-button icon="print" text="Drucker" i18n-text
                            (click)="openPrinterStatus()"
                            [badge]="printerBadge"></app-sidebar-button>

        <app-sound-control [withLabel]="true" *ngIf="layout === 'desktop'"></app-sound-control>

        <app-sidebar-button icon="settings" text="Admin" i18n-text
                            *ngIf="externalUrls.adminEnabled"
                            (click)="externalUrls.redirectToAdminPage()"></app-sidebar-button>
      </div>

      <div class="d-flex justify-content-start flex-column align-items-center pb-3">
        <div class="text-muted avatar-name rounded-circle d-flex justify-content-center align-items-center text-center">{{authUserService.authenticatedUser?.name}}</div>
        <span
          *ngIf="showVersionMismatchWarning()"
          class="material-icons-outlined version-missmatch-warning"
          title="Perform Update"
          i18n-title="@@versionMismatchModal.title"
          (click)="openVersionMismatchModal()"
        >warning</span>
        <app-sidebar-button icon="logout" text="Abmelden" i18n-text (click)="authUserService.logout()"></app-sidebar-button>
        <app-version></app-version>
      </div>
    </div>
  `,
    styles: [`
    :host {
      display: block;
      height: 100%;
    }

    .avatar-name {
      color: white !important;
      background: #c5c5c5;
      width: 50px;
      height: 50px;
      font-size: 9px;
    }

    .version-missmatch-warning {
      color: var(--bs-warning);
      font-size: 22px;
      margin-top: 10px;
      cursor: pointer;
    }
  `],
    standalone: false
})
export class SidebarComponent implements AfterViewInit {
  @Output() actionButtonPressed = new EventEmitter<void>();
  @ViewChildren(SidebarButtonComponent, { read: ElementRef }) actionButtons!: QueryList<ElementRef>;

  FEATURE_FLAGS = FEATURE_FLAGS;

constructor(
  public authUserService: AuthUserService,
  private readonly modalService: NgbModal,
  @Inject(SCREEN_LAYOUT) public layout: ScreenLayout,
  public dataService: DataService,
  private readonly router: Router,
  public readonly externalUrls: ExternalRoutesService,
  private readonly customerService: CustomerService,
  public readonly printerService: PrinterService
) { }

  get printerBadgeCount(): number {
    if (this.printerService.supportQueuedJobsClear && this.printerService.printerStatus) {
      return this.printerService.printerStatus.jobCount ?? 0;
    }
    return 0;
  }

  showVersionMismatchWarning(): boolean {
    // Only show warning on mobile devices (Android or iOS)
    if (!environment.runAsStandaloneApplication) {
      return false;
    }

    const { minAppVersion } = this.dataService;

    // If no minimum version is set, don't show warning
    if (!minAppVersion) {
      return false;
    }

    const currentVersion = version;

    // Compare semantic versions (e.g., '4.6.2')
    return this.compareVersions(currentVersion, minAppVersion) < 0;
  }

  /**
   * Compares two semantic version strings (e.g., '4.6.2')
   * @returns negative if v1 < v2, 0 if v1 === v2, positive if v1 > v2
   */
  private compareVersions(v1: string, v2: string): number {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);

    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
      const num1 = parts1[i] || 0;
      const num2 = parts2[i] || 0;

      if (num1 !== num2) {
        return num1 - num2;
      }
    }

    return 0;
  }

  get printerBadge(): number | string {
    if (this.printerService.printerStatus) {
      return this.printerService.printerStatus.printerState === 'online' ? this.printerBadgeCount : '!';
    }

    return '!';
  }

  navigateToHome() {
    this.router.navigate(['/']);
  }

  ngAfterViewInit() {
    this.actionButtons.forEach(button => {
      button.nativeElement.addEventListener('click', () => {
        this.actionButtonPressed.emit();
      });
    });
  }

  async openCashRegistry() {
    await this.modalService.open(CashRegisterActionsComponent, {
      size: 'md'
    }).result.then(result => result, () => false);
  }

  async openTransactionHistory() {
    await this.modalService.open(TransactionHistoryPage, {
      modalDialogClass: 'modal-fullscreen p-5 rounded-content',
    }).result.then(result => result, () => false);
  }

  async openAdHocProduct() {
    await this.modalService.open(AdHocProductPage, {
      size: 'lg'
    }).result.then(result => result, () => false);
  }

  async openDepositWithdraw() {
    await this.modalService.open(DepositWithdrawPage, {
      size: 'lg'
    }).result.then(result => result, () => false);
  }

  async openNewCustomerModal() {
    const newCustomerData = await this.customerService.openNewCustomerModal();
    if (!newCustomerData) {
      return;
    }
    await this.dataService.createNewCustomer(newCustomerData);
  }

  async openReservations() {
    await this.modalService.open(ReservationPage, {
      modalDialogClass: 'modal-fullscreen p-5 rounded-content',
    }).result.then(result => result, () => false);
  }

  openPrinterStatus() {
    this.modalService.open(PrinterStatusComponent);
  }

  openVersionMismatchModal() {
    this.modalService.open(VersionMismatchModalComponent);
  }
}
